// @ts-strict-ignore
import * as React from 'react';
import Highcharts, {Series, XAxisLabelsOptions} from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more';
import HighchartsPatternFill from 'highcharts/modules/pattern-fill';
import HighchartsAnnotations from 'highcharts/modules/annotations';
import ResizeObserver from 'resize-observer-polyfill';
import memoizeLast from 'just-memoize-last';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import mergeWith from 'lodash/mergeWith';
import cloneDeep from 'lodash/cloneDeep';
import {clsx} from 'clsx';

import {HoveredItem} from 'common/charts/contexts/segment-filter-controller';
import {
  getTooltipPosition,
  hoverSeries,
  patchHighchartsForNegativeLog,
  unHoverChart,
} from 'common/report/util/highcharts';
import {formatAxisLabelWithUnit, formatMetricNumber, htmlEncodeString} from 'common/util';
import {Z_TOOLTIP} from 'common/report/constants';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {MS_BY_UNIT} from 'common/util/date';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {useTranslation} from 'common/i18n/hooks';
import {IronContextRegistry} from 'common/report/context-registry';
import {useReportLayoutManager} from 'common/contexts/react/report-layout-manager-context';
import {getSeriesMatchPath} from 'common/charts/contexts/utils';
import {TextHighlight} from 'common/components/text-highlight';
import {withStyles} from 'common/util/react';

import annotationsCss from './annotations/index.inline.styl';
import {AnnotationsComponent} from './annotations/component';
// Still using inline styles because we need to figure out how to get css modules working with Highcharts.
import css from './index.inline.styl';
import {DisplayAnalysisType, DisplayPlotStyle, DisplayStackingType, TimeSeriesType, XAxisUnit} from './enums';
import {
  AnnotationState,
  FormatSeriesOptions,
  IChartDataItem,
  LineChartProps,
  LineChartYAxisOptions,
  SeriesData,
  XAxisOptions,
} from './types';
import {Annotations} from './annotations';
import {defaultXAxisLabelFormatter, formatLineChartSeries, getHighchartsColorString} from './utils';
import {OPTIONAL_PLOT_LINE_ID} from './annotations/constants';

highchartsMore(Highcharts);
patchHighchartsForNegativeLog(Highcharts);
HighchartsAnnotations(Highcharts);
HighchartsPatternFill(Highcharts);

// Highcharts prevents XSS injection on rendered elements that include user provided content.
// These configuration lines allow HTML content which is used to render chart tooltips.
// https://www.highcharts.com/docs/chart-concepts/security
Highcharts.AST.allowedReferences = [];
Highcharts.AST.allowedAttributes.push(`elref`, `icon`, `color`, `empty`, ...Object.keys(TextHighlight.attrsSchema));
Highcharts.AST.allowedTags.push(
  `style`,
  `div`,
  `span`,
  `mp-text-highlight`,
  `svg-icon`,
  `mp-button`,
  `create-annotation-button`,
);

export interface LineChartState {
  /** all state properties for annotations */
  annotations: AnnotationState;
}

export type LineChartJsxProps = {
  props: LineChartProps;
};

const AXIS_BASE_STYLES: Highcharts.CSSObject = {
  color: `var(--chart-axis-label-color)`,
  cursor: `default` as const,
  fontSize: `11px`,
  fill: `var(--chart-axis-label-color)`,
  lineHeight: `14px`,
};
export class LineChart extends React.Component<
  LineChartProps & Pick<IronContextRegistry, `i18n` | `reportLayoutManager`>,
  LineChartState
> {
  public declare annotationsRenderer: Annotations;
  declare annotationsHelpers: ReturnType<typeof Annotations.getHelpers>;

  declare container: React.RefObject<HTMLDivElement & {highcharts: Highcharts.Chart; lineChart: LineChart}>;
  private declare highcharts: Highcharts.Chart;
  private declare hoveredTimestampMs: any;
  private declare selfResizeObserver: ResizeObserver;
  private declare columnSelectedBox: Highcharts.SVGElement;
  private declare columnSelectedPoint: Highcharts.Point;
  // this is memoized to fix a bug: the chart was being re-drawn after zooming in and clicking on a
  // line chart with lots of data points, which disconnects the referenced selected point
  private declare formatSeriesMemoized: (chartData: IChartDataItem[], options: FormatSeriesOptions) => SeriesData;

  declare static defaultProps: Partial<LineChartProps>;

  constructor(props) {
    super(props);
    this.state = {
      annotations: Annotations.getDefaultState(),
    };
    this.container = React.createRef();
    this.formatSeriesMemoized = memoizeLast(this.formatSeries, isEqual);
  }

  public componentDidMount() {
    if (!this.props.chartData) {
      throw new Error(`"chartData" is a required component prop`);
    }

    this.annotationsHelpers = Annotations.getHelpers(this);

    // On resize we need to make sure the chart is reflowed to fit the new size
    this.selfResizeObserver = new ResizeObserver((entries) => {
      const {height} = entries[0].contentRect;
      if (this.props.onResize) {
        this.props.onResize(height);
      }
      this.highcharts.reflow();
      this.drawColumnSelectedBox();
    });
    // TODO: Figure out a better React way for this. Perhaps using `useResizeObserver` hook
    if (this.container) {
      this.selfResizeObserver.observe(this.container.current);
    }

    // note: this.highcharts.container may not exist in isotest environment
    if (this.highcharts) {
      this.highcharts.container?.addEventListener(`mousemove`, this.handleHighchartsContainerMouseMove);
      this.highcharts.container?.addEventListener(`mouseleave`, this.handleHighchartsContainerMouseLeave);
    }

    if (this.props.showAnnotations) {
      this.annotationsRenderer = new Annotations(this);
    }

    if (
      this.getChartData().length &&
      !this.state.annotations?.allAnnotations.size &&
      this.annotationsRenderer &&
      !this.annotationsRenderer.areLoaded()
    ) {
      this.annotationsRenderer.load(); // note: the annotations load() method implicitly requires highcharts to be initialized
    }

    /**
     * Include any plot lines passed in via options.
     * NOTE: We have to add them here and not pass them into the Highcharts
     * xAxis.plotLines object because that would be wiped out every time React
     * re-renders the chart. Due to the way we show a plot line while the user
     * hovers over the chart that would cause too many re-renders in React which
     * causes flickering in the chart. For now we will manage updating plotlines
     * by manually updating the xAxis.
     */
    if (this.props.xAxisOptions?.plotLines) {
      this.props.xAxisOptions.plotLines.forEach((plotLine) => {
        this.renderPlotLine(plotLine.value, OPTIONAL_PLOT_LINE_ID);
      });
    }
  }

  public componentDidUpdate(): void {
    if (this.props.segmentFilterController.hoveredSeries) {
      this.legendHovered(this.props.segmentFilterController.hoveredSeries);
    }
  }

  shouldComponentUpdate(): boolean {
    /**
     * Prevents re-rendering the entire chart on hover
     * interactions coming from the line chart itself.
     *
     * This is safe to introduce for the following reasons:
     * - When data loads, the whole chart is destroyed and re-rendered
     * - All interactions that need to re-render the chart happen when the user
     *   is not actively hovering over the line chart (chart customization, themes, etc..)
     */
    const hovered = this.props.segmentFilterController.hoveredSeries;
    return hovered?.sourceId !== this.props.segmentFilterController.id || hovered?.source !== `line`;
  }

  public componentWillUnmount() {
    this.selfResizeObserver.disconnect();

    if (this.annotationsRenderer) {
      this.annotationsRenderer.removeElements();
    }
    this.removeColumnSelectedBox();

    if (this.highcharts) {
      this.highcharts.container?.removeEventListener(`mousemove`, this.handleHighchartsContainerMouseMove);
      this.highcharts.container?.removeEventListener(`mouseleave`, this.handleHighchartsContainerMouseLeave);
      delete this.highcharts;
    }
  }

  afterChartCreated = (chart) => {
    // Highcharts creates a separate chart instance during export
    if (!chart?.options?.chart?.forExport) {
      this.highcharts = chart;
    }
  };

  getChartData() {
    return this.props.chartData;
  }

  // TODO: Don't expose the whole highcharts instance publicly. We should move
  // towards just passing in props/callbacks. If we really need extra info,
  // expose only a subset of it.
  getHighcharts() {
    return this.highcharts;
  }

  /** Hovers a series when the corresponding data table row is hovered */
  legendHovered = (hovered: HoveredItem) => {
    if (this.highcharts.container.style.pointerEvents === `none`) {
      // the chart is disabled, like when a popover is open
      return;
    }

    let hoveredSeries: Series[] = null;
    if (hovered?.seriesId && hovered.partialMatch) {
      const matchPath = getSeriesMatchPath(hovered.seriesId);
      hoveredSeries = this.highcharts.series.filter((s) => getSeriesMatchPath(s.name) === matchPath);
    } else if (hovered?.seriesId) {
      const series = this.highcharts.get(hovered.seriesId) as Series;
      hoveredSeries = series ? [series] : [];
    }

    if (hoveredSeries?.length) {
      hoverSeries(hoveredSeries);
    } else {
      unHoverChart(this.highcharts);
    }
  };

  getHoveredTimestampMs() {
    return this.hoveredTimestampMs;
  }

  private formatSeries(
    chartData: IChartDataItem[],
    {
      displaySeriesType,
      displayPlotStyle,
      timeUnit,
      incompleteFromDate,
      incompleteToDate,
      utcOffset,
    }: FormatSeriesOptions,
  ): SeriesData {
    return formatLineChartSeries(chartData, {
      displaySeriesType,
      displayPlotStyle,
      timeUnit,
      incompleteFromDate,
      incompleteToDate,
      utcOffset,
      onHighchartMouseOut: this.props.onHighchartMouseOut,
    });
  }

  private getYAxisOptions(
    yAxisOptions: LineChartYAxisOptions,
    axis: BookmarkTypes.AxisAssignment,
    someMetricAssignedToPrimaryAxis: boolean,
  ): LineChartYAxisOptions {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const thisComponent = this;

    const defaults =
      axis === BookmarkTypes.AxisAssignment.Primary
        ? this.props.defaultPrimaryYAxisOptions
        : this.props.defaultSecondaryYAxisOptions;

    const mergedAxisOptions = mergeWith(cloneDeep(defaults), yAxisOptions, (a, b) => (b === null ? a : undefined));

    const yAxisMax = yAxisOptions?.max ?? null;
    const yAxisMin = yAxisOptions?.min ?? null;

    // We don't want doubled up, misaligned ticks on the y-axis if we have
    // metrics assigned to both axes. Therefore, only show ticks for the
    // secondary axis if the primary axis is disabled or it has no metrics
    // assigned to it.
    const showTicks =
      axis === BookmarkTypes.AxisAssignment.Primary ||
      this.props.defaultPrimaryYAxisOptions?.labels?.enabled === false ||
      !someMetricAssignedToPrimaryAxis;

    const finalOptions: LineChartYAxisOptions = {
      ...(defaults || {}),
      ...(yAxisOptions || {}),
      allowDecimals: true,
      // Manually merge axisUnit because we want to have the default override
      // the user provided value if it's not set
      axisUnit: {
        prefix: yAxisOptions?.axisUnit?.prefix ? yAxisOptions?.axisUnit?.prefix : defaults?.axisUnit?.prefix,
        suffix: yAxisOptions?.axisUnit?.suffix ? yAxisOptions?.axisUnit?.suffix : defaults?.axisUnit?.suffix,
      },
      // If there is a user defined min or max, set corresponding
      // endOnTick/startOnTick to false to ensure that the bounds of the chart
      // match the max/min provided by the user
      endOnTick: yAxisMax === null,
      gridLineColor: null,
      gridLineDashStyle: `Solid`,
      gridLineWidth: showTicks ? 1 : 0,
      labels: {
        enabled: !this.props.hideYAxisTicks && yAxisOptions?.labels?.enabled !== false,
        ...(this.props.yAxisLabelAlignment ? {align: this.props.yAxisLabelAlignment} : {}),
        ...(this.props.yAxisLabelHorizontalOffset ? {x: this.props.yAxisLabelHorizontalOffset} : {}),
        ...(this.props.yAxisLabelVerticalOffset ? {y: this.props.yAxisLabelVerticalOffset} : {}),
        formatter(this) {
          const precision = mergedAxisOptions?.axisUnit?.precision;
          if (thisComponent.props.formatYAxisLabel) {
            /* When you have stacking set to percent (i.e. Stacked Line Chart w/
            lift) Highcharts will automatically generate y axis label values in
            the range of [0, 100]. Our formatting utils assume values in [0,1].*/
            const value =
              thisComponent.props.displayStackingType === DisplayStackingType.Percent
                ? Number(this.value) / 100
                : Number(this.value);
            return thisComponent.props.formatYAxisLabel(value, axis, yAxisOptions);
          }

          return formatAxisLabelWithUnit(
            formatMetricNumber(this.value as number, {precision, abbreviateThousands: true}),
            finalOptions.axisUnit?.prefix,
            finalOptions.axisUnit?.suffix,
          );
        },
        style: {
          ...AXIS_BASE_STYLES,
          fontFamily: mpFonts.fontFamilyMono,
        },
      },
      lineWidth: 0,
      maxPadding: yAxisOptions?.maxPadding ?? 0.05,
      minPadding: 0,
      offset: 0,
      // NOTE: We have to set the opposite values to null otherwise Highcharts
      // will hold onto the old values and not update properly.
      ...(yAxisOptions?.useSoftMinMax
        ? {max: null, min: null, softMax: yAxisMax, softMin: yAxisMin}
        : {
            max: yAxisMax,
            min: yAxisMin,
            softMax: null,
            softMin: null,
          }),
      // See: endOnTick
      startOnTick: yAxisMin === null,
      title: {
        // If we don't set this to "null" Highcharts will default to showing "Values" for the axis
        text: yAxisOptions?.title?.text || null,
      },
      type:
        yAxisOptions?.type ||
        (this.props.displayAnalysisType === DisplayAnalysisType.Logarithmic
          ? DisplayAnalysisType.Logarithmic
          : DisplayAnalysisType.Linear),
      visible: yAxisOptions?.visible ?? yAxisOptions?.labels?.enabled !== false,
      tickPositions: yAxisOptions?.tickPositions,
    };
    return finalOptions;
  }

  private getChartMinWidth(): number {
    const chartData = this.props.chartData;

    // line charts do not limit the plot width, for column charts
    // we return 1 here so that highcharts reflows the container
    // on future rendering cycles
    if (this.props.displaySeriesType !== TimeSeriesType.Column || chartData.length === 0) {
      return 1;
    }

    const MIN_COLUMN_WIDTH = 8;
    const COLUMN_GAP = 4;
    const COLUMN_GROUP_GAP = 8;

    const dataPoints = chartData[0].data.length;
    const groupCount = this.props.displayPlotStyle === DisplayPlotStyle.Stacked ? 1 : chartData.length;
    const totalColumns = dataPoints * groupCount;

    // highcharts defines group and point padding as "x axis units"
    // which is not the same as raw pixel spacing, we compute
    // an estimated pixel spacing to account for the minimum plot size
    const columnWidths = totalColumns * MIN_COLUMN_WIDTH;
    const columnSpacing = (totalColumns - 1) * COLUMN_GAP;
    const groupSpacing = groupCount === 1 ? 0 : (groupCount - 1) * COLUMN_GROUP_GAP;
    const minPlotWidth = columnWidths + columnSpacing + groupSpacing;

    return minPlotWidth;
  }

  private lastChartOptions: Highcharts.Options = null;

  private shouldUpdateAnnotations(lastChartOptions: Highcharts.Options, series: SeriesData): boolean {
    if (!this.props.showAnnotations || !this.lastChartOptions) {
      return false;
    }
    const xAxisOptions: XAxisOptions = this.props.xAxisOptions;

    // Re-render annotations if the x-axis labels are toggled
    const xAxisLabelsToggled =
      !(this.lastChartOptions.xAxis instanceof Array) &&
      // Toggle label
      xAxisOptions?.labels?.enabled !== undefined &&
      this.lastChartOptions.xAxis.labels?.enabled !== xAxisOptions?.labels?.enabled;

    const xAxisTitleToggled =
      !(this.lastChartOptions.xAxis instanceof Array) &&
      xAxisOptions?.title?.text !== this.lastChartOptions.xAxis.title?.text;

    // Re-render if the y axis assignments have changed
    const yAxisAssigmentsHaveChanged = !isEqual(
      series.map((entry) => entry.yAxis),
      lastChartOptions.series.map((entry) => entry.yAxis),
    );

    return xAxisLabelsToggled || xAxisTitleToggled || yAxisAssigmentsHaveChanged;
  }

  private getChartOptions(): Highcharts.Options {
    const series = this.formatSeriesMemoized(
      this.getChartData(),
      pick(this.props, [
        `displaySeriesType`,
        `displayPlotStyle`,
        `timeUnit`,
        `incompleteFromDate`,
        `incompleteToDate`,
        `utcOffset`,
        `onHighchartMouseOut`,
      ]),
    );
    const xAxisOptions: XAxisOptions = mergeWith({...this.props.defaultXAxisOptions}, this.props.xAxisOptions);
    if (this.shouldUpdateAnnotations(this.lastChartOptions, series)) {
      // Annotations live outside of react land but also update state so we
      // need to fire this after the current render complete.
      setTimeout(() => this.annotationsRenderer.setAnnotations(), 1);
    }

    const timeUnit = this.props.timeUnit;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const thisComponent = this;
    const isStackedChart = this.props.displayPlotStyle === DisplayPlotStyle.Stacked;
    const someMetricAssignedToPrimaryAxis = series.some((s) => s.yAxis === 0);

    const options: Highcharts.Options = {
      // TODO consider using the accessibility.js module. This silences warning in the meantime.
      accessibility: {enabled: false},
      chart: {
        animation: false,
        backgroundColor: null,
        scrollablePlotArea: {
          minWidth: this.getChartMinWidth(),
        },
        // "reflow" is normally true by default, but it only triggers on window resize events,
        // so we disable it and call reflow() manually with a ResizeObserver on this element.
        reflow: false,
        marginLeft: this.props.chartMarginLeft,
        marginRight: this.props.chartMarginRight,
        style: {
          fontFamily: mpFonts.fontFamilyPrimary,
          fontSize: `12px,`,
        },
        spacingBottom: this.props.showAnnotations ? 32 : 12,
        type: isStackedChart ? `area` : `line`,
        zooming: {
          // disables zoom on columns
          type: this.props.displaySeriesType === TimeSeriesType.Column ? null : this.props.zoomType,
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      loading: {
        labelStyle: {
          color: `transparent`,
        },
      },
      plotOptions: {
        column: {
          borderWidth: 1,
          borderColor: `var(--background-color)`, // fakes white space between stacked columns
          borderRadius: {
            scope: `point`,
            where: isStackedChart ? `all` : `end`,
            radius: 4,
          },
          // when a series value is 0, sets the minimum bar height
          minPointLength: 3,
          pointPadding: 0,
          groupPadding: 0.07,
          // when there's few data points in a chart, sets the max width of a column
          maxPointWidth: this.container.current ? this.container.current.clientWidth / 3 : 120,
          stacking: isStackedChart ? this.props.displayStackingType : null,
          stickyTracking: true,
          states: {
            select: {
              borderWidth: 1,
              // default color is gray
              color: undefined,
              // this prevents a strange flash above the column after selecting it
              enabled: false,
            },
            hover: {
              animation: false,
              borderWidth: 1,
              brightness: 0,
            },
            normal: {
              animation: false,
            },
          },
          point: {
            events: {
              select() {
                thisComponent.columnSelectedPoint = this;
                thisComponent.drawColumnSelectedBox();
              },
              unselect() {
                thisComponent.removeColumnSelectedBox();
              },
            },
          },
        },
        line: {
          ...(thisComponent.props.onHighChartClick && {cursor: `pointer`}),
          lineWidth: 2,
          marker: {
            symbol: `circle`,
            lineColor: `#fff`,
            lineWidth: 2,
            radius: 5,
          },
          shadow: false,
          states: {
            hover: {
              animation: false,
            },
            normal: {
              // Setting duration to 25ms instead of `animation: false` prevents all lines flashing
              // into the normal state when hovering between densely packed lines (bug in highcharts).
              animation: {duration: 25},
            },
          },
        },
        scatter: {
          ...(thisComponent.props.onHighChartClick && {cursor: `pointer`}),
          lineWidth: 0,
          marker: {
            symbol: `circle`,
            lineColor: `#fff`,
            lineWidth: 0,
            radius: 5,
            states: {
              select: {
                lineColor: `#fff`,
                fillColor: null,
                lineWidth: 3,
                radius: 7,
              },
            },
          },
          states: {
            hover: {
              animation: false,
            },
            normal: {
              // Setting duration to 25ms instead of `animation: false` prevents all lines flashing
              // into the normal state when hovering between densely packed lines (bug in highcharts).
              animation: {duration: 25},
            },
          },
        },
        arearange: {
          enableMouseTracking: !this.props.disableAreaRangeMouseTracking,
        },
        area: {
          ...(thisComponent.props.onHighChartClick && {cursor: `pointer`}),
          lineWidth: 0,
          marker: {
            symbol: `circle`,
            lineColor: `#fff`,
            lineWidth: 2,
            radius: 5,
          },
          // default 0.75
          fillOpacity: 0.85,
          shadow: false,
          states: {
            hover: {
              lineWidthPlus: 0,
            },
            normal: {
              animation: {duration: 200},
            },
            inactive: {
              opacity: 0.3,
            },
          },
          trackByArea: true,
          stickyTracking: false,
        },
        series: {
          animation: false,
          // specify stacking as undefined, there seems to be a bug when switching from area => line
          // while the chart is still rendered where the stacking from area applies to line.
          stacking: undefined,
          states: {
            inactive: {enabled: true, animation: false},
            select: {
              halo: {
                size: 10,
                opacity: 0.25,
              },
            },
          },
          marker: {
            enabled: false,
            lineWidth: 2,
            symbol: `circle`,
            states: {
              select: {
                lineColor: `#fff`,
                fillColor: null,
                lineWidth: 3,
                radius: 7,
              },
            },
          },
          point: {
            events: {
              mouseOver() {
                thisComponent.updateHoveredTimestampMs(this.x);
                const {color, series: mouseOverSeries} = this;
                const colorString = getHighchartsColorString(color);
                thisComponent.props.segmentFilterController.notifySeriesHovered({
                  seriesId: mouseOverSeries.name,
                  source: `line`,
                });
                return thisComponent.props.onHighchartMouseOver?.({
                  highchartPoint: this,
                  highchartChart: thisComponent.highcharts,
                  color: colorString,
                });
              },
              mouseOut() {
                thisComponent.props.segmentFilterController.notifySeriesHovered(null);
                return thisComponent.props.onHighchartMouseOut?.(thisComponent.highcharts);
              },
              click(event) {
                return thisComponent.props.onHighChartClick?.(event.point, event);
              },
            },
          },
          shadow: false,
          ...(isStackedChart && {
            stacking: this.props.displayStackingType,
          }),
        },
      },
      // @ts-ignore (type is checked in `formatSeries`, but had to omit 'type' key there to allow either 'area' or 'line')
      series,
      time: {
        useUTC: false,
      },
      tooltip: {
        enabled: !thisComponent.props.disableTooltip,
        backgroundColor: null,
        borderWidth: 0,
        formatter(this) {
          // Highcharts callbacks don't work with arrow functions
          const {index, name, color} = this.series;
          const colorString = getHighchartsColorString(color);
          const x = this.x as number;

          // Override style to make the tooltip not look like the default
          if (thisComponent.props.formatTooltip) {
            return thisComponent.props.formatTooltip(x, this.y, this.series, index, this);
          }

          const formattedLeftLabel = thisComponent.props.formatPointX
            ? thisComponent.props.formatPointX(x)
            : Highcharts.dateFormat(`%b %e, %Y`, x);
          const formattedValue = thisComponent.props.formatPointY
            ? thisComponent.props.formatPointY(this.y)
            : this.y.toLocaleString();
          const seriesLabel = name;

          const defaultLabelFormatter = `
            <div class="row">
              <span class="value" style="font-weight: 700">${htmlEncodeString(formattedLeftLabel)}:</span>
              <span class="value">${formattedValue}</span>
            </div>
          `;
          return `
            <div class="line-chart-tooltip">
              <div class="title" style="background-color: ${colorString};">${seriesLabel}</div>
              <div class="result">
                ${defaultLabelFormatter}
              </div>
            </div>
          `;
        },
        hideDelay: 0,
        // Tooltips get appended to the body so they're not constrained by containers
        outside: true,
        padding: 0,
        positioner(this, tooltipWidth, tooltipHeight, point) {
          const chart = this.chart;
          return getTooltipPosition({
            tooltipWidth,
            tooltipHeight,
            pointX: point.plotX,
            pointY: point.plotY,
            chartLeft: chart.plotLeft,
            chartTop: chart.plotTop,
            chartWidth: chart.plotWidth,
            chartHeight: chart.plotHeight,
          });
        },
        shadow: false,
        shape: `rect`,
        useHTML: true,
        style: {
          zIndex: Z_TOOLTIP,
        },
      },
      title: {
        text: null,
      },
      xAxis: {
        endOnTick: false,
        labels: {
          enabled: (xAxisOptions?.labels as XAxisLabelsOptions)?.enabled !== false,
          distance: 6, // makes enough space for annotations
          staggerLines: xAxisOptions?.labels?.staggerLines,
          step: xAxisOptions?.labels?.step,
          style: AXIS_BASE_STYLES,
          ...(xAxisOptions?.labels?.style ? {style: xAxisOptions?.labels?.style} : {}),
          ...(this.props.formatXAxisLabel
            ? {
                formatter(this) {
                  return thisComponent.props.i18n.print(
                    thisComponent.props.formatXAxisLabel(this.value as number, timeUnit),
                  );
                },
              }
            : {
                formatter(this) {
                  if (xAxisOptions?.unit === XAxisUnit.Number) {
                    return this.value as string;
                  }
                  return defaultXAxisLabelFormatter(this.value as number, timeUnit);
                },
              }),
        },
        crosshair: xAxisOptions?.crosshair,
        lineWidth: this.props.hideChartLines ? 0 : 1,
        maxPadding: 0,
        minPadding: 0,
        minTickInterval: MS_BY_UNIT[this.props.timeUnit],
        plotBands: xAxisOptions?.plotBands,
        tickPosition: `outside`,
        tickPositions: xAxisOptions?.tickPositions,
        tickPositioner(this) {
          const {dataMax, dataMin} = this.getExtremes();
          return thisComponent.props.formatTicksX ? thisComponent.props.formatTicksX(dataMin, dataMax) : undefined;
        },
        tickLength: (xAxisOptions?.labels as XAxisLabelsOptions)?.enabled !== false ? 6 : 0,
        tickWidth: this.props.hideChartLines ? 0 : 1,
        tickmarkPlacement: `on`,
        title: {
          text:
            (xAxisOptions?.labels as XAxisLabelsOptions)?.enabled !== false
              ? xAxisOptions?.title?.text || xAxisOptions?.title?.placeholder
              : null,
        },
        type: xAxisOptions?.unit === XAxisUnit.Number ? `linear` : `datetime`,
      },
      yAxis: [
        this.getYAxisOptions(
          this.props.primaryYAxisOptions,
          BookmarkTypes.AxisAssignment.Primary,
          someMetricAssignedToPrimaryAxis,
        ),
        ...(this.props.secondaryYAxisOptions
          ? [
              {
                ...this.getYAxisOptions(
                  this.props.secondaryYAxisOptions,
                  BookmarkTypes.AxisAssignment.Secondary,
                  someMetricAssignedToPrimaryAxis,
                ),
                opposite: true,
              },
            ]
          : []),
      ],
    };

    this.lastChartOptions = options;
    return options;
  }
  // bind method with arrow syntax because it's expected to be called from within a Highcharts callback
  updateHoveredTimestampMs = (x: null | number) => {
    if (this.hoveredTimestampMs === x || this.state.annotations?.creatingAnnotation) {
      return;
    }

    this.hoveredTimestampMs = x;
    if (this.annotationsRenderer) {
      this.annotationsRenderer.redrawCreateButton();
    }
  };

  // bind method with arrow syntax because it's expected to be used as a DOM event listener
  handleHighchartsContainerMouseMove = (ev: MouseEvent) => {
    if (this.state.annotations?.creatingAnnotation) {
      return;
    }

    // If hovering over x-axis labels (which is naturally the area below the minimum y-value), manually update the "create annotation" hover position
    const yAxis = this.highcharts.yAxis[0];
    const y = yAxis.toValue(ev.offsetY, false);
    if (y < yAxis.min) {
      const extendedMouseEvent = this.highcharts.pointer.normalize(ev);
      const nearestPoint = this.highcharts.pointer.findNearestKDPoint(
        this.highcharts.series,
        false,
        extendedMouseEvent,
      );
      if (nearestPoint && this.hoveredTimestampMs !== nearestPoint.x) {
        this.updateHoveredTimestampMs(nearestPoint.x);
      }
    }
  };

  // bind method with arrow syntax because it's expected to be used as a DOM event listener
  handleHighchartsContainerMouseLeave = () => {
    if (this.state.annotations?.creatingAnnotation === false) {
      this.updateHoveredTimestampMs(null);
    }
  };

  renderPlotLine(timestampMs: number, plotLineId: string) {
    const xAxis = this.getHighcharts().xAxis[0];
    xAxis.addPlotLine({
      value: timestampMs,
      width: 1,
      id: plotLineId,
    });
  }

  removePlotLine(plotLineId: string) {
    const xAxis = this.getHighcharts().xAxis[0];
    xAxis.removePlotLine(plotLineId);
  }

  /** Adds an active state for selected bar, similar to funnel steps */
  drawColumnSelectedBox() {
    const point = this.columnSelectedPoint;
    if (!point) {
      return;
    }
    this.columnSelectedBox?.destroy();

    const chart = point.series.chart;
    const spread = 8;
    // @ts-ignore Property 'parentGroup' does not exist on type 'SVGElement'
    const parentGroup: Highcharts.SVGElement = point.graphic.parentGroup;
    const x = point.shapeArgs.x - spread / 2;
    const y = point.shapeArgs.y - spread / 2;
    const w = point.shapeArgs.width + spread;
    const h = point.shapeArgs.height + spread;
    const r = 6;
    const fill = `rgba(var(--chart-highlight-color-rgb), 0.3)`;
    const zIndex = -1;

    // @ts-ignore Property 'stacking' does not exist on type 'SeriesOptionsType'.
    if (point.series.options.stacking) {
      this.columnSelectedBox = chart.renderer
        .rect(
          // if we move rendering outside of the group, add chart.plotLeft and chart.plotTop respectively
          point.shapeArgs.x - spread / 2,
          point.shapeArgs.y - spread / 2,
          point.shapeArgs.width + spread,
          point.shapeArgs.height + spread,
          6,
          1,
        )
        .attr({fill, zIndex});
    } else {
      // We manually draw paths because rect objects cannot have rounded corners on just one side,
      // which we need when negative bars are present
      this.columnSelectedBox = chart.renderer
        .path(
          // @ts-ignore Property 'negative' does not exist on type 'Point'
          point.negative
            ? [
                [`M`, x, y],
                [`L`, x + w, y],
                [`L`, x + w, y + h - r],
                [`A`, r, r, 0, 0, 1, x + w - r, y + h], // bottom-right corner rounded
                [`L`, x + r, y + h],
                [`A`, r, r, 0, 0, 1, x, y + h - r], // bottom-left corner rounded
                [`L`, x, y],
                [`Z`],
              ]
            : [
                [`M`, x + r, y],
                [`L`, x + w - r, y],
                [`A`, r, r, 0, 0, 1, x + w, y + r], // top-right corner rounded
                [`L`, x + w, y + h],
                [`L`, x, y + h],
                [`L`, x, y + r],
                [`A`, r, r, 0, 0, 1, x + r, y], // top-left corner rounded
                [`Z`],
              ],
        )
        .attr({fill, zIndex, 'stroke-width': 1});
    }
    this.columnSelectedBox.add(parentGroup);
  }

  removeColumnSelectedBox() {
    this.columnSelectedPoint = undefined;
    this.columnSelectedBox?.destroy();
    this.columnSelectedBox = undefined;
  }

  render() {
    if (this.container && this.selfResizeObserver) {
      this.selfResizeObserver.observe(this.container.current);
    }
    const hasAnnotations = this.props.showAnnotations && this.state.annotations;
    // Hacky shim for maintaining existing tests. Ideally we find a better way
    // to test highcharts rather than inspecting the data/state on it.
    if (this.container?.current) {
      this.container.current.highcharts = this.highcharts;
      this.container.current.lineChart = this;
    }

    const containerClasses = clsx(`${this.props.displaySeriesType}-chart`, {
      stacked: this.props.displayPlotStyle === DisplayPlotStyle.Stacked,
    });

    return (
      <div elref='highchartsContainer' className={this.props.className} style={{height: `100%`}} ref={this.container}>
        {hasAnnotations ? <style>{annotationsCss}</style> : null}
        <div>
          {this.props.showAnnotations && this.state.annotations ? (
            <AnnotationsComponent
              annotations={this.state.annotations}
              annotationsHelpers={this.annotationsHelpers}
              showAnnotations={this.props.showAnnotations}
              timeUnit={this.props.timeUnit}
              utcOffset={this.props.utcOffset}
            />
          ) : null}
        </div>
        <HighchartsReact
          callback={(c: Highcharts.Chart) => this.afterChartCreated(c)}
          containerProps={{style: {height: `100%`}, className: containerClasses}}
          highcharts={Highcharts}
          options={this.getChartOptions()}
          // useful for board perf optimization. in fast-edit mode. we need to disallow chart updates so it doesn't re-flow on every single card
          allowChartUpdate={this.props.allowChartUpdate ?? true}
        />
      </div>
    );
  }
}

LineChart.defaultProps = {
  displayStackingType: DisplayStackingType.Normal,
};

const LineChartWithStyles = withStyles(LineChart, css);

export const LineChartReact = React.forwardRef<LineChart, LineChartProps>((props, ref) => {
  const i18n = useTranslation();
  const reportLayoutManager = useReportLayoutManager();

  return <LineChartWithStyles ref={ref} i18n={i18n} reportLayoutManager={reportLayoutManager} {...props} />;
});
